import { useRef, useEffect } from 'react'

export type ServiceCategory = 'taxi' | 'ultima' | 'child'

interface ServiceTab {
  id: ServiceCategory
  label: string
  tariffs: string[] // Какие классы тарифов показывать
}

const SERVICE_TABS: ServiceTab[] = [
  {
    id: 'taxi',
    label: 'ТАКСИ',
    tariffs: ['lite_b2b', 'standart_b2b', 'optimum_b2b', 'econom', 'comfort', 'comfortplus', 'business', 'minivan']
  },
  {
    id: 'ultima',
    label: 'УЛЬТИМА',
    tariffs: ['business', 'vip', 'premier', 'ultimate', 'elite', 'maybach', 'premium_van']
  },
  {
    id: 'child',
    label: 'С ДЕТЬМИ',
    tariffs: ['child_tariff', 'comfort', 'comfortplus']
  },
]

interface ServiceTabsProps {
  activeTab: ServiceCategory
  onTabChange: (tab: ServiceCategory) => void
}

export const ServiceTabs = ({ activeTab, onTabChange }: ServiceTabsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLButtonElement>(null)

  // Скролл к активной вкладке
  useEffect(() => {
    if (activeRef.current && scrollRef.current) {
      const container = scrollRef.current
      const element = activeRef.current
      const containerWidth = container.offsetWidth
      const elementLeft = element.offsetLeft
      const elementWidth = element.offsetWidth
      
      const scrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2
      container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' })
    }
  }, [activeTab])

  return (
    <div 
      ref={scrollRef}
      className="flex gap-1 overflow-x-auto hide-scrollbar py-2 border-b border-primary/10"
    >
      {SERVICE_TABS.map((tab) => {
        const isActive = activeTab === tab.id
        return (
          <button
            key={tab.id}
            ref={isActive ? activeRef : null}
            onClick={() => onTabChange(tab.id)}
            className={`
              flex-shrink-0 px-5 py-2.5 text-sm font-semibold tracking-wide
              transition-all duration-200 relative
              ${isActive
                ? 'text-white'
                : 'text-text-muted hover:text-text-secondary'
              }
            `}
          >
            {tab.label}
            {/* Индикатор активной вкладки */}
            {isActive && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-white rounded-full" />
            )}
          </button>
        )
      })}
    </div>
  )
}

// Получить список тарифов для категории
export const getTariffsForCategory = (category: ServiceCategory): string[] => {
  const tab = SERVICE_TABS.find(t => t.id === category)
  return tab?.tariffs || []
}

// Фильтровать тарифы по категории
export const filterTariffsByCategory = <T extends { class: string }>(
  tariffs: T[],
  category: ServiceCategory
): T[] => {
  const allowedClasses = getTariffsForCategory(category)
  return tariffs.filter(t => allowedClasses.includes(t.class))
}

// Определить категорию по классу тарифа
export const getCategoryByTariffClass = (tariffClass: string): ServiceCategory => {
  // Ультима тарифы
  if (['vip', 'premier', 'ultimate', 'elite', 'maybach', 'premium_van'].includes(tariffClass)) {
    return 'ultima'
  }
  // Детские тарифы
  if (['child_tariff'].includes(tariffClass)) {
    return 'child'
  }
  // Все остальные - такси
  return 'taxi'
}

export default ServiceTabs
